import { ExternalLink } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { UI_STRINGS } from '@/lib/constants/ui-strings';

interface BrowsePublicPagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * "Leaving the Startup Profile View" confirmation (MYN-58). Composes `Dialog`
 * directly rather than `ConfirmDialog`: this is a hero-style, centered dialog
 * (icon + centered heading via Dialog's `align="center"`/`headerIcon`) with
 * two STACKED full-width CTAs - not the horizontal Cancel/Confirm pair
 * `ConfirmDialog` hardcodes into its `footer` slot - so the buttons live in
 * `children` (mnr-standards: "feature dialogs compose Dialog / ConfirmDialog
 * plus their own body").
 */
export function BrowsePublicPagesDialog({ open, onOpenChange, onConfirm }: BrowsePublicPagesDialogProps) {
  const S = UI_STRINGS.SHARED_PROFILE;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      align="center"
      title={S.BROWSE_POPUP_HEADING}
      titleClassName="text-center text-xl font-bold text-navy"
      headerIcon={
        <span
          aria-hidden="true"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-yellow text-navy"
        >
          <ExternalLink size={24} />
        </span>
      }
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <p className="text-sm text-subtext">{S.BROWSE_POPUP_BODY}</p>
        <p className="font-sans text-sm font-semibold text-navy">{S.BROWSE_POPUP_CONFIRM}</p>
        <div className="flex w-full flex-col gap-3">
          <Button type="button" variant="navy" className="w-full py-3" onClick={onConfirm}>
            {S.BROWSE_POPUP_CONTINUE}
          </Button>
          <Button
            type="button"
            variant="green"
            className="w-full py-3"
            onClick={() => onOpenChange(false)}
          >
            {S.BROWSE_POPUP_STAY}
          </Button>
        </div>
        <p className="text-[11px] text-subtext">{S.BROWSE_POPUP_FOOTNOTE}</p>
      </div>
    </Dialog>
  );
}
